import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchAuth } from '../services/authService';

const API = process.env.REACT_APP_API_URL;

function fmt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function Spinner() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#475569', fontSize: 13 }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
        style={{ animation: 'spin 0.8s linear infinite' }}>
        <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0" opacity=".25"/>
        <path d="M21 12a9 9 0 0 0-9-9"/>
      </svg>
      Loading…
    </div>
  );
}

function Badge({ color, bg, border, children }) {
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 5,
      color, background: bg, border: `1px solid ${border}`,
      letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  );
}

function Section({ title, icon, count, loading, error, empty, children }) {
  return (
    <div style={{
      background: 'rgba(30,41,59,0.6)', backdropFilter: 'blur(16px)',
      border: '1px solid rgba(71,158,195,0.15)', borderRadius: 16, overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '18px 24px', borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(71,158,195,0.1)', border: '1px solid rgba(71,158,195,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#479EC3', flexShrink: 0 }}>
          {icon}
        </div>
        <span style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9' }}>{title}</span>
        {count != null && (
          <span style={{ marginLeft: 4, fontSize: 12, fontWeight: 700, color: '#479EC3', background: 'rgba(71,158,195,0.12)', border: '1px solid rgba(71,158,195,0.25)', borderRadius: 6, padding: '2px 8px' }}>
            {count}
          </span>
        )}
      </div>
      <div className="adm-section-pad" style={{ padding: '16px 24px' }}>
        {loading ? <Spinner /> : error ? (
          <p style={{ fontSize: 13, color: '#f87171' }}>{error}</p>
        ) : empty ? (
          <p style={{ fontSize: 13, color: '#334155' }}>Nothing here — all clear.</p>
        ) : children}
      </div>
    </div>
  );
}

const PLANS = ['free', 'starter', 'growth', 'pro'];

function ActionBtn({ onClick, busy, color, bg, border, hoverBg, children }) {
  return (
    <button onClick={onClick} disabled={!!busy} style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '5px 12px', borderRadius: 7, border: `1px solid ${border}`,
      background: busy ? 'rgba(30,41,59,0.5)' : bg,
      color: busy ? '#334155' : color,
      fontSize: 12, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer',
      transition: 'background .15s', whiteSpace: 'nowrap', fontFamily: 'inherit',
    }}
      onMouseEnter={e => { if (!busy) e.currentTarget.style.background = hoverBg; }}
      onMouseLeave={e => { if (!busy) e.currentTarget.style.background = bg; }}
    >
      {busy ? (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ animation: 'spin 0.8s linear infinite', flexShrink: 0 }}>
          <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0" opacity=".25"/><path d="M21 12a9 9 0 0 0-9-9"/>
        </svg>
      ) : null}
      {children}
    </button>
  );
}

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [orgs, setOrgs]           = useState([]);
  const [orgsLoading, setOrgsLoading]   = useState(true);
  const [orgsError, setOrgsError]       = useState('');
  const [orgBusy, setOrgBusy]     = useState({});
  const [orgPlan, setOrgPlan]     = useState({});
  const [orgRowError, setOrgRowError] = useState({});

  const [users, setUsers]         = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError]     = useState('');
  const [userBusy, setUserBusy]   = useState({});
  const [userRowError, setUserRowError] = useState({});

  async function approveCompany(companyId) {
    const plan = orgPlan[companyId] || 'free';
    setOrgBusy(b => ({ ...b, [companyId]: 'approve' }));
    setOrgRowError(e => ({ ...e, [companyId]: '' }));
    try {
      const res = await fetchAuth(`${API}/admin/pulse/approve-company`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId, plan }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to approve company.');
      setOrgs(prev => prev.filter(o => o.companyId !== companyId));
    } catch (err) {
      setOrgRowError(e => ({ ...e, [companyId]: err.message }));
    } finally {
      setOrgBusy(b => ({ ...b, [companyId]: null }));
    }
  }

  async function denyCompany(companyId, companyName) {
    if (!window.confirm(`Permanently delete "${companyName}" and all its data? This cannot be undone.`)) return;
    setOrgBusy(b => ({ ...b, [companyId]: 'deny' }));
    setOrgRowError(e => ({ ...e, [companyId]: '' }));
    try {
      const res = await fetchAuth(`${API}/admin/pulse/deny-company`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to deny company.');
      setOrgs(prev => prev.filter(o => o.companyId !== companyId));
    } catch (err) {
      setOrgRowError(e => ({ ...e, [companyId]: err.message }));
    } finally {
      setOrgBusy(b => ({ ...b, [companyId]: null }));
    }
  }

  async function approveUser(companyId, userId) {
    setUserBusy(b => ({ ...b, [userId]: 'approve' }));
    setUserRowError(e => ({ ...e, [userId]: '' }));
    try {
      const res = await fetchAuth(`${API}/admin/pulse/approve-user`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId, userId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to approve user.');
      setUsers(prev => prev.filter(u => u.userId !== userId));
    } catch (err) {
      setUserRowError(e => ({ ...e, [userId]: err.message }));
    } finally {
      setUserBusy(b => ({ ...b, [userId]: null }));
    }
  }

  async function denyUser(companyId, userId, name) {
    if (!window.confirm(`Delete user "${name || userId}"? This cannot be undone.`)) return;
    setUserBusy(b => ({ ...b, [userId]: 'deny' }));
    setUserRowError(e => ({ ...e, [userId]: '' }));
    try {
      const res = await fetchAuth(`${API}/admin/pulse/deny-user`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId, userId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to deny user.');
      setUsers(prev => prev.filter(u => u.userId !== userId));
    } catch (err) {
      setUserRowError(e => ({ ...e, [userId]: err.message }));
    } finally {
      setUserBusy(b => ({ ...b, [userId]: null }));
    }
  }

  const load = useCallback(async () => {
    setOrgsLoading(true);
    setUsersLoading(true);
    setOrgsError('');
    setUsersError('');

    const safeJson = async (res) => { try { return await res.json(); } catch { return null; } };

    const [orgsRes, usersRes] = await Promise.all([
      fetchAuth(`${API}/admin/pulse/pending-orgs`).catch(() => null),
      fetchAuth(`${API}/admin/pulse/inactive-users`).catch(() => null),
    ]);

    if (!orgsRes || orgsRes.status === 401 || orgsRes.status === 403) {
      navigate('/dashboard', { replace: true });
      return;
    }

    if (!orgsRes.ok) {
      setOrgsError('Failed to load pending orgs.');
    } else {
      const data = await safeJson(orgsRes);
      setOrgs(Array.isArray(data) ? data : []);
    }
    setOrgsLoading(false);

    if (!usersRes || usersRes.status === 401 || usersRes.status === 403) {
      navigate('/dashboard', { replace: true });
      return;
    }
    if (!usersRes.ok) {
      setUsersError('Failed to load inactive users.');
    } else {
      const data = await safeJson(usersRes);
      setUsers(Array.isArray(data) ? data : []);
    }
    setUsersLoading(false);
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ background: '#0f172a', color: '#e2e8f0', minHeight: '100vh', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @media (max-width: 540px) {
          .adm-email   { display: none !important; }
          .adm-divider { display: none !important; }
          .adm-topbar  { padding: 0 14px !important; }
          .adm-main    { padding: 24px 14px 60px !important; }
          .adm-section-pad { padding: 14px 14px !important; }

          .adm-table thead { display: none; }
          .adm-table, .adm-table tbody { display: block; }
          .adm-table tr {
            display: block;
            margin-bottom: 10px;
            border-radius: 10px;
            border: 1px solid rgba(255,255,255,0.06);
            background: rgba(15,23,42,0.5);
            padding: 12px 14px;
          }
          .adm-table tr:last-child { margin-bottom: 0; }
          .adm-table td {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 5px 0 !important;
            border: none !important;
            font-size: 13px;
            gap: 8px;
          }
          .adm-table td::before {
            content: attr(data-label);
            color: #475569;
            font-size: 11px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            flex-shrink: 0;
          }
        }
      `}</style>

      {/* Grid bg */}
      <div style={{ position: 'fixed', inset: 0, opacity: 0.04, backgroundImage: 'linear-gradient(to right,#22D3EE 1px,transparent 1px),linear-gradient(to bottom,#22D3EE 1px,transparent 1px)', backgroundSize: '64px 64px', pointerEvents: 'none' }} />

      {/* Top bar */}
      <div style={{ position: 'sticky', top: 0, zIndex: 100, background: 'rgba(15,23,42,0.9)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="adm-topbar" style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px', height: 64, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 7, background: 'linear-gradient(135deg,#6366f1,#479EC3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Admin Panel</span>
            <Badge color="#818cf8" bg="rgba(99,102,241,0.1)" border="rgba(99,102,241,0.25)">Pulse</Badge>
          </div>

          <div style={{ flex: 1 }} />

          <span className="adm-email" style={{ fontSize: 12, color: '#475569' }}>{user?.email}</span>
          <div className="adm-divider" style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.08)' }} />
          <button onClick={load} style={{ background: 'rgba(71,158,195,0.1)', border: '1px solid rgba(71,158,195,0.2)', borderRadius: 7, padding: '5px 12px', color: '#479EC3', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
            Refresh
          </button>
          <button onClick={logout} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#475569', display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 600 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            Sign Out
          </button>
        </div>
      </div>

      <main className="adm-main" style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 24px 80px', position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: 24 }}>

        {/* Pending Orgs */}
        <Section
          title="Pending Organisations"
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>}
          count={orgsLoading ? null : orgs.length}
          loading={orgsLoading}
          error={orgsError}
          empty={!orgsLoading && !orgsError && orgs.length === 0}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {orgs.map(org => {
              const busy = orgBusy[org.companyId];
              return (
                <div key={org.companyId} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                    padding: '14px 18px', borderRadius: 10,
                    background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(255,255,255,0.06)',
                  }}>
                    <div style={{ flex: 1, minWidth: 160 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9', marginBottom: 2 }}>{org.companyName}</div>
                      <div style={{ fontSize: 12, color: '#475569', fontFamily: 'monospace' }}>{org.companyId}</div>
                    </div>
                    <div style={{ fontSize: 12, color: '#334155' }}>{fmt(org.createdAt)}</div>

                    {/* Plan selector */}
                    <select
                      value={orgPlan[org.companyId] || 'free'}
                      onChange={e => setOrgPlan(p => ({ ...p, [org.companyId]: e.target.value }))}
                      disabled={!!busy}
                      style={{
                        background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(71,158,195,0.25)',
                        borderRadius: 7, color: '#94a3b8', fontSize: 12, padding: '5px 8px',
                        cursor: 'pointer', fontFamily: 'inherit', outline: 'none',
                      }}
                    >
                      {PLANS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                    </select>

                    <ActionBtn
                      onClick={() => approveCompany(org.companyId)}
                      busy={busy === 'approve'}
                      color="#10b981" bg="rgba(16,185,129,0.1)" border="rgba(16,185,129,0.25)" hoverBg="rgba(16,185,129,0.18)"
                    >
                      {busy === 'approve' ? 'Approving…' : 'Approve'}
                    </ActionBtn>
                    <ActionBtn
                      onClick={() => denyCompany(org.companyId, org.companyName)}
                      busy={busy === 'deny'}
                      color="#f87171" bg="rgba(239,68,68,0.08)" border="rgba(239,68,68,0.2)" hoverBg="rgba(239,68,68,0.16)"
                    >
                      {busy === 'deny' ? 'Denying…' : 'Deny'}
                    </ActionBtn>
                  </div>
                  {orgRowError[org.companyId] && (
                    <p style={{ fontSize: 12, color: '#f87171', margin: '0 4px' }}>{orgRowError[org.companyId]}</p>
                  )}
                </div>
              );
            })}
          </div>
        </Section>

        {/* Inactive Users */}
        <Section
          title="Inactive Users"
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>}
          count={usersLoading ? null : users.length}
          loading={usersLoading}
          error={usersError}
          empty={!usersLoading && !usersError && users.length === 0}
        >
          <div style={{ overflowX: 'auto' }}>
            <table className="adm-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  {['Name', 'Email', 'Company', 'Signed up', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11.5, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const busy = userBusy[u.userId];
                  return (
                    <tr key={u.userId} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td data-label="Name" style={{ padding: '12px 12px', color: '#e2e8f0', fontWeight: 600, whiteSpace: 'nowrap' }}>{u.name || '—'}</td>
                      <td data-label="Email" style={{ padding: '12px 12px', color: '#94a3b8' }}>{u.email}</td>
                      <td data-label="Company" style={{ padding: '12px 12px', color: '#64748b', fontFamily: 'monospace', fontSize: 12 }}>{u.companyId}</td>
                      <td data-label="Signed up" style={{ padding: '12px 12px', color: '#475569', whiteSpace: 'nowrap' }}>{fmt(u.createdAt)}</td>
                      <td data-label="Status" style={{ padding: '12px 12px' }}>
                        {u.emailVerified
                          ? <Badge color="#10b981" bg="rgba(16,185,129,0.08)" border="rgba(16,185,129,0.2)">Verified</Badge>
                          : <Badge color="#f87171" bg="rgba(239,68,68,0.08)" border="rgba(239,68,68,0.2)">Unverified</Badge>}
                      </td>
                      <td data-label="Actions" style={{ padding: '12px 12px' }}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <ActionBtn
                            onClick={() => approveUser(u.companyId, u.userId)}
                            busy={busy === 'approve'}
                            color="#10b981" bg="rgba(16,185,129,0.1)" border="rgba(16,185,129,0.25)" hoverBg="rgba(16,185,129,0.18)"
                          >
                            {busy === 'approve' ? 'Approving…' : 'Approve'}
                          </ActionBtn>
                          <ActionBtn
                            onClick={() => denyUser(u.companyId, u.userId, u.name)}
                            busy={busy === 'deny'}
                            color="#f87171" bg="rgba(239,68,68,0.08)" border="rgba(239,68,68,0.2)" hoverBg="rgba(239,68,68,0.16)"
                          >
                            {busy === 'deny' ? 'Denying…' : 'Deny'}
                          </ActionBtn>
                          {userRowError[u.userId] && (
                            <span style={{ fontSize: 11, color: '#f87171', alignSelf: 'center' }}>{userRowError[u.userId]}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>

      </main>
    </div>
  );
}
